import { Box } from '@mui/material';

import { ReactComponent as LucideIcon } from '../../lab-test/v2/Icons/LucideIcon.svg';
import { ReactComponent as ApprovedIcon } from '../../lab-test/v2/Icons/approved.svg';
import { ReactComponent as DiscountIcon } from '../../lab-test/v2/Icons/Discount.svg';
import styles from './LabCard.module.scss';
import React, { useRef, useState, useLayoutEffect } from 'react';

import { ReactComponent as FastConfirmationIcon } from '../../lab-test/v2/Icons/fast-confirmation.svg';
import { ReactComponent as ExpressReportIcon } from '../../lab-test/v2/Icons/express-report-icon.svg';
import { ReactComponent as OnTimeIcon } from '../../lab-test/v2/Icons/on-time.svg';
import { ReactComponent as LocationOutlined } from '../../lab-test/v2/Icons/Location-Outlined.svg';
import { ReactComponent as PremiumIcon } from '../../lab-test/v2/Icons/Premium.svg';
import { ReactComponent as RedStrip } from '../../lab-test/v2/Icons/red-strip.svg';
import DefaultLabImage from '../../lab-test/v2/Images/DefaultLab.png';
import useSessionStorage from '../../lab-test/v2/hooks/useStorage';

const LabCard = ({
  ele,
  onViewTests,
  onBook,
  onViewBreakdown,
  onPackageSelect,
}) => {
  const {
    name,
    certificate,
    testsType,
    pickupAddress,
    testCount,
    testCountLabel,
    isHomeCollection,
    isOnTimeCollection,
    isFastConfirmation,
    preDiscountPrice,
    postDiscountPrice,
    isSameDaySlots,
    imageUrl,
    address,
    distance,
    meta,
    isFastestReport,
    partnerRemarksForPatient,
    centerId,
    isPremium,
    recommendedPackages,
    recommendationMessage,
    recommendationFound,
  } = ele;

  const [expandedRemarks, setExpandedRemarks] = useState(false);
  const [offersExpanded, setOffersExpanded] = useState(false);

  const { getFromStorage } = useSessionStorage();

  // Unsaved address (current location / skipped save) never reaches the cart
  // via select-address, so the BE pickup address is stale — show the one from
  // session storage instead.
  const selectedAddress = getFromStorage('selected_address');
  const unsavedPickupAddress =
    selectedAddress && !selectedAddress.addressId
      ? selectedAddress.formattedAddress || selectedAddress.address
      : null;

  // Home collection: saved address → BE pickupAddress, unsaved → session
  // storage. Centre visits always show the BE centre address.
  let displayAddress = address;
  if (isHomeCollection) {
    displayAddress = unsavedPickupAddress || pickupAddress;
  }

  const [isAddressExpanded, setIsAddressExpanded] = useState(false);
  const [isAddressOverflowing, setIsAddressOverflowing] = useState(false);
  const addressRef = useRef(null);

  useLayoutEffect(() => {
    if (addressRef.current) {
      const el = addressRef.current;
      setIsAddressOverflowing(el.scrollHeight > el.offsetHeight + 1);
    }
  }, [displayAddress]);

  const handleAddressClick = () => {
    let lat, lng;
    try {
      if (meta) {
        const decoded = JSON.parse(atob(meta));
        if (decoded.partnerInfo?.centerLat) {
          lat = decoded.partnerInfo.centerLat;
          lng = decoded.partnerInfo.centerLng;
        } else if (decoded.lat) {
          lat = decoded.lat;
          lng = decoded.long;
        }
      }
    } catch {}

    if (lat && lng) {
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
        '_blank'
      );
    } else {
      const query = address || pickupAddress;
      if (query)
        window.open(
          `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
            query
          )}`,
          '_blank'
        );
    }
  };

  const testTypeLabel = testsType
    ? testsType.charAt(0).toUpperCase() + testsType.slice(1)
    : 'Pathology';

  const activeFeatures = [];
  if (isPremium) {
    activeFeatures.push({ text: 'Premium Partner', Icon: PremiumIcon });
  }
  if (isFastestReport)
    activeFeatures.push({ text: 'Express Report', Icon: ExpressReportIcon });
  if (isFastConfirmation)
    activeFeatures.push({
      text: 'Fast Confirmation',
      Icon: FastConfirmationIcon,
    });
  if (isOnTimeCollection)
    activeFeatures.push({ text: 'On-time Collection', Icon: OnTimeIcon });

  const displayedFeatures = activeFeatures.slice(0, 2);
  const hasFeatures = displayedFeatures.length > 0;

  return (
    <div className={styles.cardWrapper}>
      <div
        onClick={() => {
          if (onBook && typeof onBook === 'function') onBook(ele);
        }}
        className={`${styles.cardRoot} ${recommendationFound ? styles.cardRootOffers : ''}`}
      >
        <span className={styles.cardTitle}>{name}</span>

        <Box
          className={`${styles.topBox} ${isPremium ? styles.topBoxPremium : ''}`}
          style={{
            borderRadius:
              !partnerRemarksForPatient && !hasFeatures && !recommendationFound
                ? '12px'
                : '12px 12px 0px 0px',
          }}
        >
          <Box className={styles.labLogo}>
            <img
              src={imageUrl ?? DefaultLabImage}
              alt="Lab Logo"
              className={styles.logo}
            />
          </Box>

          {certificate && (
            <div className={styles.certificateBadge}>
              <RedStrip />
              <span className={styles.badgeText}>{certificate}</span>
            </div>
          )}

          <Box className={styles.collectionWrap}>
            <div className={styles.collectionPill}>
              <ApprovedIcon />
              <span className={styles.pillText}>
                {isSameDaySlots ? 'Same-Day ' : ''}
                {!centerId ? 'Home Collection' : 'Centre Visit'}
              </span>
            </div>
          </Box>

          {/* ADDRESS */}
          <div className={styles.addressRow}>
            {displayAddress && (
              <div className={styles.addressSection}>
                <div className={styles.addressHeaderRow}>
                  <span className={styles.addressTitle}>
                    {!centerId ? 'Pickup Address' : 'Centre Address'}
                  </span>
                  {centerId && <div className={styles.seprator} />}
                  {centerId && (
                    <div
                      className={styles.distanceContainer}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddressClick();
                      }}
                    >
                      <LocationOutlined
                        width="14"
                        height="14"
                        color={'var(--primary-button, #714fff)'}
                      />
                      <span className={styles.distanceText}>
                        {distance?.toFixed(1)} km
                      </span>
                    </div>
                  )}
                </div>

                <div className={styles.addressWrapper}>
                  <span
                    ref={addressRef}
                    className={`${styles.addressText} ${
                      isAddressExpanded ? styles.addressExpanded : ''
                    }`}
                  >
                    {displayAddress}
                  </span>

                  {isAddressOverflowing && (
                    <span
                      className={styles.showMoreText}
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsAddressExpanded((p) => !p);
                      }}
                    >
                      {isAddressExpanded ? 'Show Less' : 'Show More'}
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className={styles.testInfoSection}>
              <span className={styles.pill}>{testTypeLabel}</span>

              {(testCountLabel || testCount > 0) && (
                <div
                  className={styles.testsIncludedWrapper}
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewTests?.(ele);
                  }}
                >
                  <span className={styles.testTypeText}>
                    {testCountLabel || `${testCount} Tests included`}
                  </span>
                </div>
              )}
            </div>
          </div>
        </Box>

        {/* FEATURES */}
        {hasFeatures && (
          <Box
            className={styles.featureStrip}
            style={{
              borderRadius: !partnerRemarksForPatient
                ? '0px 0px 12px 12px'
                : '0',
            }}
          >
            {displayedFeatures.map((feature, index) => (
              <React.Fragment key={index}>
                <div className={styles.featureItem}>
                  <feature.Icon width={14} height={14} />
                  <span>{feature.text}</span>
                </div>
                {index < displayedFeatures.length - 1 && (
                  <div className={styles.dot}></div>
                )}
              </React.Fragment>
            ))}
          </Box>
        )}

        {/* REMARKS */}
        {partnerRemarksForPatient && (
          <Box className={styles.remarksBox}>
            <span className={styles.remarksTitle}>
              Important centre remarks*
            </span>

            <div className={styles.remarksContent}>
              <span
                className={`${styles.remarksText} ${
                  expandedRemarks ? styles.remarksExpanded : ''
                }`}
              >
                {partnerRemarksForPatient}
              </span>

              <span
                className={styles.remarksToggle}
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedRemarks((p) => !p);
                }}
              >
                {expandedRemarks ? 'Show Less' : '... Show More'}
              </span>
            </div>
          </Box>
        )}

        {/* PRICE */}
        <Box className={styles.priceRow}>
          <Box
            className={styles.youPayWrap}
            onClick={(e) => {
              e.stopPropagation();
              onViewBreakdown?.(ele);
            }}
          >
            <span className={styles.youPayText}>You Pay</span>
            <LucideIcon width="24px" height="24px" />
          </Box>

          <Box display="flex" gap={1} alignItems="center">
            {preDiscountPrice && preDiscountPrice != postDiscountPrice ? (
              <span className={styles.preDiscountPrice}>
                ₹{preDiscountPrice}
              </span>
            ) : null}
            <span className={styles.postDiscountPrice}>
              ₹{postDiscountPrice}
            </span>
          </Box>
        </Box>
      </div>
      {recommendationFound && (
        <div className={styles.offersRoot}>
          {/* collapsed strip */}
          <div
            className={`${styles.offersStrip} ${offersExpanded ? styles.offersStripExpanded : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              setOffersExpanded((p) => !p);
            }}
          >
            <DiscountIcon
              width={16}
              height={16}
              style={{
                '--discount-icon-fill': 'var(--primary-button, #714fff)',
              }}
            />
            <span className={styles.offersMessage}>
              {recommendationMessage}
            </span>
            <span className={styles.viewMoreText}>
              {offersExpanded ? 'View Less' : 'View More'}
            </span>
          </div>

          {/* expanded accordion */}
          {offersExpanded && (
            <div
              className={styles.offersExpanded}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.packagesRow}>
                {(recommendedPackages || []).slice(0, 2).map((pkg) => (
                  <div
                    key={pkg.packageId}
                    className={`${styles.packageCardWrap} ${recommendedPackages.length === 1 ? styles.packageCardWrapFull : ''}`}
                  >
                    <div
                      className={`${styles.packageCard} ${pkg.isRecommended ? styles.packageCardHighlighted : ''} ${recommendedPackages.length === 1 ? styles.packageCardSingle : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onPackageSelect?.(pkg, ele);
                      }}
                    >
                      {pkg.isRecommended && (
                        <div className={styles.recommendedPill}>
                          <span className={styles.recommendedPillText}>
                            Recommended
                          </span>
                        </div>
                      )}
                      <span className={styles.packageName}>
                        {pkg.userMessage}
                      </span>
                      <div className={styles.packageCardBottom}>
                        <span className={styles.packageTestCount}>
                          {pkg.packageTestCount} Tests included
                        </span>
                        <div className={styles.packageArrow}>
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                          >
                            <path
                              d="M4.5 9L7.5 6L4.5 3"
                              stroke="var(--Color-Icon-Intense, #100E15)"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LabCard;
